import { ClipperOffset, EndType, JoinType, Path64, Point64 } from 'clipper2-js';
import { BufferAttribute, Matrix4, Shape, ShapeUtils as ThreeShapeUtils, Vector2 } from 'three';

const INT_SCALAR = 1e9;

// Monkey patch Shape to add a retract method that uses
// https://github.com/IRobot1/clipper2-ts/blob/eefbad501c878c3c28ecd39f7af210ba9b53c937/projects/clipper2-js/src/lib/offset.ts#L400
// issue: https://github.com/IRobot1/clipper2-ts/issues/4
(ClipperOffset.prototype as any).buildNormals = function (this: ClipperOffset, path: Path64) {
  const cnt = path.length;
  (this as any)._normals = [];

  for (let i = 0; i < cnt - 1; i++) {
    (this as any)._normals[i] = (ClipperOffset as any).getUnitNormal(path[i], path[i + 1]);
  }
  (this as any)._normals[cnt - 1] = (ClipperOffset as any).getUnitNormal(path[cnt - 1], path[0]);
};

export class ShapeUtils {
  /**
   * Calculate area of a ( 2D ) contour polygon.
   */
  static area(contour: Vector2[]): number {
    return ThreeShapeUtils.area(contour);
  }

  /**
   * Note that this is a linear function so it is necessary to calculate separately for x, y components of a polygon.
   * @remarks Used internally by {@link THREE.Path | Path}, {@link THREE.ExtrudeGeometry | ExtrudeGeometry} and {@link THREE.ShapeGeometry | ShapeGeometry}.
   */
  static isClockWise(pts: Vector2[]): boolean {
    return ThreeShapeUtils.isClockWise(pts);
  }

  /**
   * Used internally by {@link THREE.ExtrudeGeometry | ExtrudeGeometry} and {@link THREE.ShapeGeometry | ShapeGeometry} to calculate faces in shapes with holes.
   */
  static triangulateShape(contour: Vector2[], holes: Vector2[][]): number[][] {
    return ThreeShapeUtils.triangulateShape(contour, holes);
  }

  /**
   * Convert a clipper2-js path to vector2 points the scale is 1e6 by default.
   * due to the clipper2-js library using a fixed point number system.
   */
  static clipperPathToPoints = (path: Path64, scale = INT_SCALAR): Array<Vector2> => {
    return path.map((item) => new Vector2(item.x / scale, item.y / scale));
  };

  static pointsToClipperPath = (points: Vector2[], scale = INT_SCALAR): Path64 => {
    return points.map((point) => ({ x: point.x * scale, y: point.y * scale }));
  };

  // /**
  //  * Offset the shape by its normals.
  //  *
  //  * @see https://codesandbox.io/p/sandbox/three-clipper-example-fg4lp9?file=%2Fsrc%2Findex.ts%3A267%2C3-274%2C4
  //  * @see https://websvg.github.io/voronoi/
  //  * @see https://github.com/WebSVG/voronoi/blob/8893768e3929ea713a47dba2c4d273b775e0bd82/src/voronoi_diag.js#L278
  //  * @see https://angusj.com/clipper2/Docs/Units/Clipper.Offset/Classes/ClipperOffset/_Body.htm
  //  */
  static offsetShape(shape: Shape, offset: number) {
    const contour = shape.getPoints();
    const target = shape.clone();
    let result = [];

    const geometry = new BufferAttribute(new Float32Array([-offset, 0, 0]), 3);

    for (let i = 0; i < contour.length; i++) {
      let v1 = new Vector2().subVectors(
        contour[i - 1 < 0 ? contour.length - 1 : i - 1],
        contour[i]
      );
      let v2 = new Vector2().subVectors(contour[i + 1 === contour.length ? 0 : i + 1], contour[i]);
      let angle = v2.angle() - v1.angle();
      let halfAngle = angle * 0.5;

      let hA = halfAngle;
      let tA = v2.angle() + Math.PI * 0.5;

      let shift = Math.tan(hA - Math.PI * 0.5);
      // prettier-ignore
      let shiftMatrix = new Matrix4().set(
             1, 0, 0, 0,
        -shift, 1, 0, 0,
             0, 0, 1, 0,
             0, 0, 0, 1
      );

      let tempAngle = tA;
      // prettier-ignore
      let rotationMatrix = new Matrix4().set(
        Math.cos(tempAngle), -Math.sin(tempAngle), 0, 0,
        Math.sin(tempAngle),  Math.cos(tempAngle), 0, 0,
                          0,                    0, 1, 0,
                          0,                    0, 0, 1
      );
      // prettier-ignore
      let translationMatrix = new Matrix4().set(
        1, 0, 0, contour[i].x,
        0, 1, 0, contour[i].y,
        0, 0, 1, 0,
        0, 0, 0, 1,
      );

      let cloneOffset = geometry.clone();

      cloneOffset.applyMatrix4(shiftMatrix);
      cloneOffset.applyMatrix4(rotationMatrix);
      cloneOffset.applyMatrix4(translationMatrix);

      const newPoint = new Vector2(cloneOffset.getX(0), cloneOffset.getY(0));

      result.push(newPoint);
    }

    target.setFromPoints(result);

    return target;
  }

  static offsetShapeClipper(shape: Shape, offset: number) {
    const contour = shape.getPoints();
    const path = ShapeUtils.pointsToClipperPath(contour);
    const solution = [] as Path64[];
    const offsetClipper = new ClipperOffset();
    offsetClipper.addPath(path as Point64[], JoinType.Miter, EndType.Polygon);
    offsetClipper.execute(offset * INT_SCALAR, solution);

    const offsetPoints = solution.map((path) => ShapeUtils.clipperPathToPoints(path));

    const target = shape.clone();
    target.setFromPoints(offsetPoints.flat());

    return target;
  }

  static roundedRectShape(width: number, height: number, radius: number): Shape {
    const shape = new Shape();
    const x = -(width / 2);
    const y = -(height / 2);
    shape.moveTo(x, y + radius);
    shape.lineTo(x, y + height - radius);
    shape.quadraticCurveTo(x, y + height, x + radius, y + height);
    shape.lineTo(x + width - radius, y + height);
    shape.quadraticCurveTo(x + width, y + height, x + width, y + height - radius);
    shape.lineTo(x + width, y + radius);
    shape.quadraticCurveTo(x + width, y, x + width - radius, y);
    shape.lineTo(x + radius, y);
    shape.quadraticCurveTo(x, y, x, y + radius);
    return shape;
  }
}
